import * as React from 'react'

import {
  MAX_SESSION_AGE_MS,
  THRESHOLD_FOR_SCORE_INCREASE,
} from '../utils/commentScoreConstants'
import { getScoreSessionKeys } from '../utils/commentScoreSessionManager'

export const useCommentScoreTracker = (
  currentScore: number | null,
  channelIds: string[],
): {
  hasScoreIncreased: boolean
  scoreDelta: number
  startSession: () => void
  stopSession: () => void
} => {
  // `startScore` is tracked in state (not a ref) so that `hasScoreIncreased`
  // and `scoreDelta` can be derived synchronously in the same render as
  // `currentScore` updates. A ref here would require a useEffect to recompute,
  // which introduces an extra render cycle and causes the empty state to
  // flash a "default" state between score-arrival and increase-detection.
  const [startScore, setStartScore] = React.useState<number | null>(null)
  const currentScoreRef = React.useRef<number | null>(currentScore)
  const retryTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const pendingSessionStartRef = React.useRef(false)

  const { scoreKey, timestampKey } = getScoreSessionKeys(channelIds)

  const { scoreDelta, hasScoreIncreased } = React.useMemo(() => {
    if (
      currentScore != null &&
      startScore != null &&
      currentScore > startScore
    ) {
      const delta = currentScore - startScore
      return {
        scoreDelta: delta,
        hasScoreIncreased: delta >= THRESHOLD_FOR_SCORE_INCREASE,
      }
    }
    return { scoreDelta: 0, hasScoreIncreased: false }
  }, [currentScore, startScore])

  // Keep ref updated with latest currentScore
  React.useEffect(() => {
    currentScoreRef.current = currentScore

    // If we have a pending session start and score just loaded, create the session now
    if (pendingSessionStartRef.current && currentScore != null) {
      pendingSessionStartRef.current = false

      // Clear any pending retry
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }

      // Create session immediately with the newly loaded score
      const existingScore = localStorage.getItem(scoreKey)
      const existingTimestamp = localStorage.getItem(timestampKey)
      const now = Date.now()

      if (existingScore !== null && existingTimestamp !== null) {
        const timestamp = parseInt(existingTimestamp, 10)
        const isExpired = now - timestamp > MAX_SESSION_AGE_MS
        if (!isExpired) {
          return
        }
      }

      localStorage.setItem(scoreKey, currentScore.toString())
      localStorage.setItem(timestampKey, now.toString())
      setStartScore(currentScore)
    }
  }, [currentScore, scoreKey, timestampKey])

  const resetScore = React.useCallback((): void => {
    localStorage.removeItem(scoreKey)
    localStorage.removeItem(timestampKey)
    setStartScore(null)
  }, [scoreKey, timestampKey])

  const startSession = React.useCallback((): void => {
    // Use ref to get the latest currentScore value
    const latestScore = currentScoreRef.current

    const existingScore = localStorage.getItem(scoreKey)
    const existingTimestamp = localStorage.getItem(timestampKey)
    const now = Date.now()

    if (existingScore !== null && existingTimestamp !== null) {
      const timestamp = parseInt(existingTimestamp, 10)
      const isExpired = now - timestamp > MAX_SESSION_AGE_MS
      if (!isExpired) {
        return
      }
      // Clear stale session
      resetScore()
    }

    if (latestScore != null) {
      localStorage.setItem(scoreKey, latestScore.toString())
      localStorage.setItem(timestampKey, now.toString())
      setStartScore(latestScore)
      pendingSessionStartRef.current = false
    } else {
      // Score is loading - mark as pending and wait for score to load via useEffect
      pendingSessionStartRef.current = true
    }
  }, [scoreKey, timestampKey, resetScore])

  const stopSession = React.useCallback((): void => {
    resetScore()
  }, [resetScore])

  // Rehydrate from localStorage on mount
  React.useEffect(() => {
    const stored = localStorage.getItem(scoreKey)
    const storedTimestamp = localStorage.getItem(timestampKey)

    if (stored && storedTimestamp) {
      const timestamp = parseInt(storedTimestamp, 10)
      const now = Date.now()

      const isExpired = now - timestamp > MAX_SESSION_AGE_MS
      if (isExpired) {
        resetScore()
        return
      }

      setStartScore(parseInt(stored, 10))
    }
  }, [scoreKey, timestampKey, resetScore])

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return (): void => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
    }
  }, [])

  return { hasScoreIncreased, scoreDelta, startSession, stopSession }
}
